import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames'; 
import { Routes } from '../../navigation/routes';
import { routeHistory } from '@nackle/biw-web-router';
import { Row, Col } from 'antd';
import { PageAlert, InfoBlock, ActionButton, Link } from '../common';
import { Translate } from '../../services';
import './styles.less';

export const CookieAlert = ( props ) => {
    const [ isAllowed, setRouteAllowance ] = useState( true );
    const [ isVisible, setVisible ] = useState( false );
    const [ wasClosed, setClosed ] = useState( false );

    const updateAllowance = useCallback( ( _action, nextRoute, _prevRoute ) => {
        setRouteAllowance( !nextRoute?.match?.hideCookieAlert );
    }, [] );

    const handleRouteChange = useCallback( ( action, nextRoute, prevRoute ) => {
        updateAllowance( action, nextRoute, prevRoute );

        // Every time the route changes, and the alert was previously closed without
        // acceptance, consider the alert no longer closed (while still respecting
        // the other visibility rules).
        if ( wasClosed ) {
            setClosed( false );
        }
    }, [ updateAllowance, wasClosed, setClosed ] );

    useEffect( () => {
        // Trigger the `handleRouteChanged` callback every time the route changes.
        routeHistory.addEventListener( 'changed', handleRouteChange );
        return () => { routeHistory.removeEventListener( 'changed', handleRouteChange ); };
    }, [ handleRouteChange ] );

    /**
     * Triggers the reveal of the alert if the alert is currently hidden.
     * @type { function }
     */
    const showAlert = useCallback( () => {
        if ( !isVisible ) {
            setTimeout( () => { setVisible( true ); }, 300 );
        }
    }, [ isVisible, setVisible ] );

    /**
     * Triggers the hiding of the alert if the alert is currently visible.
     * @type { function }
     */
    const hideAlert = useCallback( () => {
        if ( isVisible ) {
            setVisible( false );
        }
    }, [ isVisible, setVisible ] );

    /**
     * Handles what happens when the user accepts cookies.
     * @type { function }
     */
    const handleAccept = useCallback( () => {
        // Store acceptance of cookies in local storage.
        props.acceptanceStorage.setItem( props.acceptanceKey, true );
        hideAlert( false );
    }, [ hideAlert, props.acceptanceStorage, props.acceptanceKey ] );

    useEffect( () => {
        // Determine whether or not the user has already accepted the cookie notice.
        const cookiesAccepted = props.acceptanceStorage.getItem( props.acceptanceKey );
        const shouldHide = cookiesAccepted || wasClosed || ( props.hidden || !isAllowed );

        if ( shouldHide && isVisible ) {
            hideAlert();
        } else if ( !shouldHide && !isVisible ) {
            showAlert();
        }
    }, [ hideAlert, showAlert, isVisible, isAllowed, wasClosed, props.acceptanceStorage, props.acceptanceKey, props.hidden ] );

    // Construct the classes to use for the root component.
    const classes = classnames(
        'cookie-alert',
        { [ props.className ]: Boolean( props.className ) },
    );

    return (
        <PageAlert
            className={ classes }
            onClose={ () => { hideAlert(); setClosed( true ); } }
            visible={ isVisible }
        >
            <InfoBlock>
                <InfoBlock.Header animate id="cookie-use">
                    <Translate.Text value="cookieAlert.header" />
                </InfoBlock.Header>
                <InfoBlock.Body>
                    <Row>
                        <Col xs={ 24 } md={ 24 } className="cookie-alert-info-section">
                            <Translate.Text value="cookieAlert.body" variables={ { cookiePolicyLink: (
                                <Link key="cookie-policy-link" to={ Routes.CookiePolicy.buildPath() } >
                                    <Translate.Text value="cookieAlert.body.cookiePolicyLink" />
                                </Link>
                            ) } } />
                        </Col>
                    
                        <Col xs={ 24 } md={ 24 } className="cookie-alert-action-section">
                            <ActionButton
                                className="accept-button"
                                onClick={ handleAccept }
                                important  
                            >
                                <Translate.Text value="cookieAlert.actions.accept" />
                            </ActionButton>
                        </Col>
                        </Row>
                </InfoBlock.Body>
            </InfoBlock>
        </PageAlert> 
    );
};

CookieAlert.defaultProps = {
    className: undefined,
    hidden: false,
    acceptanceStorage: localStorage,
    acceptanceKey: 'cookies-accepted',
};

CookieAlert.propTypes = {
    className: PropTypes.string,
    hidden: PropTypes.bool,
    acceptanceStorage: PropTypes.any,
    acceptanceKey: PropTypes.string,
};
